import { Box, Divider, Skeleton } from '@chakra-ui/react';
import { useEffect } from 'react';
import { AutoSizer, Column, Table } from 'react-virtualized';

import { Alert, AlertIcon, Heading } from 'components/v4';
import useVirtualizedTableFilter from 'hooks/useVirtualizedTableFilter';
import { useSelector } from 'react-redux';

import TransferTableSearchBar from './TransferTableSearchBar';
import ColumnAutoSize from './ColumnAutoSize';
import CheckboxCell from './CheckboxCell';

const TransferTable = ({
    checkedArray = [],
    onClick = () => {},
    data = [],
    captionName,
    isLoading,
    placeholder = '',
    isDisabled,
    colDefs = [],
    searchField = 'telephoneNumber', // Add default search field prop
    tableHeight = 480,
}) => {
    const { darkMode } = useSelector((state) => {
        return { ...state.settings };
    });
    const { handleChangeSearch, setSearchField, filteredData, tableData } =
        useVirtualizedTableFilter({ data });

    useEffect(() => {
        setSearchField(searchField);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchField]);

    return (
        <Box
            className="min-w-[500px] max-w-[500px]"
            sx={{
                '.ReactVirtualized__Table__rowColumn': {
                    padding: 'var(--chakra-space-2)',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                },
                '.ReactVirtualized__Table__headerColumn': {
                    padding: 'var(--chakra-space-2)',
                    color: darkMode ? 'white' : 'inherit',
                },
                '.ReactVirtualized__Table__row:nth-of-type(odd)': {
                    background: darkMode
                        ? '#263238'
                        : 'var(--chakra-colors-messenger-100)',
                },
                '.ReactVirtualized__Table__row': {
                    background: darkMode
                        ? 'var(--chakra-colors-dark-tcap)'
                        : 'inherit',
                },
            }}>
            <Heading className="block min-h-[50px] pb-0 pt-2" fontSize="2xl">
                {captionName} ({tableData.length})
            </Heading>
            <Box className="py-2">
                <TransferTableSearchBar
                    onSearch={handleChangeSearch}
                    isDisabled={data.length === 0 || isDisabled}
                    placeholder={placeholder}
                    captionName={captionName}
                />
            </Box>
            <AutoSizer key="transferTable" disableHeight>
                {({ width }) => (
                    <Table
                        width={width}
                        height={tableHeight}
                        headerHeight={41.5}
                        headerStyle={{ display: 'flex' }}
                        rowHeight={40}
                        onRowClick={(e) => {
                            if (isDisabled) return;
                            onClick(e.rowData);
                        }}
                        noRowsRenderer={() => {
                            return isLoading ? (
                                [...Array(15)].map((item, index) => (
                                    <div key={index}>
                                        <Skeleton h={9} />
                                        <Divider h={1} />
                                    </div>
                                ))
                            ) : (
                                <Alert status="info">
                                    <AlertIcon />
                                    No records to display
                                </Alert>
                            );
                        }}
                        rowCount={isLoading ? 0 : filteredData.length}
                        rowGetter={({ index }) => filteredData[index]}>
                        {colDefs.length > 0
                            ? colDefs.map((col, index) => {
                                  return col.isCheckbox ? (
                                      <Column
                                          key={`checkbox-${index}`}
                                          className="truncate-none"
                                          label={col.label}
                                          cellRenderer={CheckboxCell}
                                          dataKey="disabled"
                                          columnData={{
                                              onClick,
                                              checkedArray,
                                          }}
                                      />
                                  ) : (
                                      <Column
                                          key={col.dataKey}
                                          label={col.label}
                                          dataKey={col.dataKey}
                                          cellRenderer={({
                                              dataKey,
                                              parent,
                                              rowData,
                                              rowIndex,
                                          }) => {
                                              // If custom cellRenderer is provided, use it
                                              if (col.cellRenderer) {
                                                  const content =
                                                      col.cellRenderer({
                                                          rowData,
                                                          dataKey,
                                                      });
                                                  return (
                                                      <div
                                                          style={{
                                                              padding: '8px',
                                                              display: 'flex',
                                                              alignItems:
                                                                  'center',
                                                              height: '100%',
                                                              overflow:
                                                                  'hidden',
                                                          }}>
                                                          <span
                                                              style={{
                                                                  overflow:
                                                                      'hidden',
                                                                  textOverflow:
                                                                      'ellipsis',
                                                                  whiteSpace:
                                                                      'nowrap',
                                                                  width: '100%',
                                                              }}>
                                                              {content}
                                                          </span>
                                                      </div>
                                                  );
                                              }
                                              // Otherwise use default ColumnAutoSize
                                              return (
                                                  <ColumnAutoSize
                                                      dataKey={dataKey}
                                                      parent={parent}
                                                      rowData={rowData}
                                                      rowIndex={rowIndex}
                                                      isPhoneNumber={
                                                          dataKey ===
                                                          'telephoneNumber'
                                                      }
                                                  />
                                              );
                                          }}
                                          minWidth={col.minWidth}
                                          flexGrow={col.flexGrow}
                                          maxWidth={col.maxWidth}
                                          columnData={col.columnData}
                                      />
                                  );
                              })
                            : null}
                    </Table>
                )}
            </AutoSizer>
        </Box>
    );
};
export default TransferTable;
